<template>
  <div class="main-page-editor">
    <!-- FR-016: vuedraggable로 섹션 순서 재정렬 (hero/cta는 이동 불가) -->
    <draggable
      v-model="sectionOrderArray"
      handle=".drag-handle"
      :move="handleMove"
      @end="handleDragEnd"
      item-key="key"
      ghost-class="section-ghost"
    >
      <template #item="{ element: sectionKey }">
        <section-wrapper
          :section-key="sectionKey"
          :section-name="getSectionName(sectionKey)"
          :draggable="isDraggable(sectionKey)"
          @edit="handleEditSection(sectionKey)"
        >
          <component
            :is="getSectionComponent(sectionKey)"
            :data="getSectionData(sectionKey)"
          />
        </section-wrapper>
      </template>
    </draggable>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import draggable from 'vuedraggable';
import SectionWrapper from './SectionWrapper.vue';
import SectionHero from './sections/SectionHero.vue';
import SectionFeatureCards from './sections/SectionFeatureCards.vue';
import SectionStats from './sections/SectionStats.vue';
import SectionPartners from './sections/SectionPartners.vue';
import SectionPress from './sections/SectionPress.vue';
import SectionCTA from './sections/SectionCTA.vue';

// 섹션 이름 매핑
const SECTION_NAMES = {
  hero: '히어로 배너',
  features: '서비스 특징',
  stats: '통계 카운터',
  partners: '파트너/고객사',
  press: '보도자료',
  cta: 'CTA 문의',
};

// 컴포넌트 매핑
const SECTION_COMPONENTS = {
  hero: 'SectionHero',
  features: 'SectionFeatureCards',
  stats: 'SectionStats',
  partners: 'SectionPartners',
  press: 'SectionPress',
  cta: 'SectionCTA',
};

// 위치 고정 섹션 (드래그 불가) - hero는 index 0, cta는 마지막 고정
const FIXED_SECTIONS = ['hero', 'cta'];

export default {
  name: 'MainPageEditor',

  components: {
    draggable,
    SectionWrapper,
    SectionHero,
    SectionFeatureCards,
    SectionStats,
    SectionPartners,
    SectionPress,
    SectionCTA,
  },

  computed: {
    ...mapGetters('homepageEditor', ['currentPageData']),

    // vuedraggable v-model 바인딩용 - Vuex state와 로컬 배열 동기화
    sectionOrderArray: {
      get() {
        return this.currentPageData?.sectionOrder || [
          'hero', 'features', 'stats', 'partners', 'press', 'cta',
        ];
      },
      set(newOrder) {
        // @end 이벤트에서 커밋하므로 여기서는 별도 처리 없음
        // (vuedraggable이 내부 상태 관리, 커밋은 handleDragEnd에서)
        void newOrder;
      },
    },
  },

  methods: {
    ...mapMutations('homepageEditor', ['SET_ACTIVE_EDITOR', 'UPDATE_SECTION_ORDER']),

    getSectionName(key) {
      return SECTION_NAMES[key] || key;
    },

    getSectionComponent(key) {
      return SECTION_COMPONENTS[key] || null;
    },

    isDraggable(key) {
      return !FIXED_SECTIONS.includes(key);
    },

    // 섹션 데이터 조회
    getSectionData(sectionKey) {
      if (!this.currentPageData?.sections) return {};

      // press 섹션은 별도 press 배열 사용
      if (sectionKey === 'press') {
        const articles = this.currentPageData.press || [];
        return { articles };
      }

      const section = this.currentPageData.sections.find(
        (s) => s.sectionKey === sectionKey
      );
      return section?.data || {};
    },

    // FR-016: 드래그 이동 가능 여부 검사
    // - dragged 요소가 hero/cta이면 이동 불가
    // - related 요소가 hero(index 0) 위 또는 cta(마지막) 아래로 이동하면 불가
    handleMove(evt) {
      const draggedKey = evt.draggedContext.element;
      const relatedKey = evt.relatedContext.element;
      const futureIndex = evt.draggedContext.futureIndex;
      const list = evt.relatedContext.list || this.sectionOrderArray;

      // hero/cta는 드래그 자체를 막음
      if (FIXED_SECTIONS.includes(draggedKey)) {
        return false;
      }

      // hero(index 0) 위치로는 이동 불가 (futureIndex 0 차단)
      if (futureIndex === 0) {
        return false;
      }

      // cta(마지막) 위치로는 이동 불가
      if (futureIndex >= list.length - 1) {
        return false;
      }

      // 이동 대상 위치에 hero/cta가 있는 경우 차단
      if (relatedKey && FIXED_SECTIONS.includes(relatedKey)) {
        return false;
      }

      return true;
    },

    // 드래그 완료 시 Vuex mutation으로 순서 저장
    handleDragEnd() {
      // vuedraggable이 sectionOrderArray를 이미 업데이트한 상태
      // computed get()에서 반환된 배열을 참조하여 Vuex에 커밋
      const newOrder = [...this.sectionOrderArray];

      // hero는 항상 첫 번째, cta는 항상 마지막 위치 보장
      const heroIdx = newOrder.indexOf('hero');
      const ctaIdx = newOrder.indexOf('cta');

      if (heroIdx !== 0) {
        newOrder.splice(heroIdx, 1);
        newOrder.unshift('hero');
      }
      if (ctaIdx !== newOrder.length - 1) {
        newOrder.splice(newOrder.indexOf('cta'), 1);
        newOrder.push('cta');
      }

      this.UPDATE_SECTION_ORDER(newOrder);
    },

    // 편집 버튼 클릭 시 Vuex 상태 업데이트 (Phase 3에서 Drawer/Modal 연결)
    handleEditSection(sectionKey) {
      const drawerSections = ['hero', 'stats', 'partners', 'cta'];
      const type = drawerSections.includes(sectionKey) ? 'drawer' : 'modal';
      this.SET_ACTIVE_EDITOR({ type, sectionKey });
    },
  },
};
</script>

<style scoped>
.main-page-editor {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 드래그 중인 고스트 요소 스타일 */
.section-ghost {
  opacity: 0.85;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  transform: rotate(1deg);
}
</style>
